import { Action } from 'history';
import { useEffect } from 'react';
import { useHistory } from 'react-router-dom';

import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';

export enum VizLoadType {
  ThumbnailClick = 'thumbnail_click',
  DirectUrl = 'direct_url',
  EmailLink = 'email_link',
  SlackLink = 'slack_link',
  Redirect = 'redirect',
  BrowserHistory = 'browser_history',
  BrowserRefresh = 'browser_refresh',
  DevicePreviewOpen = 'device_preview_open',
  DevicePreviewDeviceChange = 'device_preview_device_change',
  DevicePreviewViewChange = 'device_preview_view_change',
  RevisionHistory = 'revision_history',
  AdminViz = 'admin_viz',
}

interface VizLoadContext {
  readonly timestamp: number;
  readonly loadType: VizLoadType;
}

let vizLoadContext: VizLoadContext | null = null;

export const VizLoadContextService = {
  set(loadType: VizLoadType, timestamp: number = Date.now()): void {
    if (!FeatureFlags.vpVizLoadMeasurementEnabled) { return; }
    vizLoadContext = { timestamp, loadType };
  },

  has(): boolean {
    return vizLoadContext !== null;
  },

  // Returns the current context and clears it. Returns null if not set.
  consume(): VizLoadContext | null {
    const ctx = vizLoadContext;
    vizLoadContext = null;
    return ctx;
  },

  // Set viz load type to BrowserHistory when the user navigates back, forward, or replaces
  // the current page.
  recordNavigation(action: Action): void {
    if (action === 'POP' && !VizLoadContextService.has()) {
      VizLoadContextService.set(VizLoadType.BrowserHistory);
    }
  }
};

// Hook — must be rendered inside a <Router>. Wire up in RouterHooksContainer.
// It tags viz loads with navigation source, so downstream telemetry can distinguish
// history-navigation loads from direct URL, thumbnail click, redirect, etc.
export function useHistoryNavigationTracker(): void {
  const history = useHistory();

  useEffect(() => {
    if (!FeatureFlags.vpVizLoadMeasurementEnabled) { return; }

    const unlisten = history.listen((_location, action) => {
      VizLoadContextService.recordNavigation(action);
    });

    return unlisten;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
